import * as ex from 'excalibur';
import { ActionType, EntityType } from '../types/enums';
import { Action, GridPosition, EntityConfig } from '../types/interfaces';
import { ANIMATION_DURATION, TILE_SIZE } from '../config/constants';
import { Grid } from './grid';

// Base Entity class for all grid entities
export class Entity extends ex.Actor {
  public gridX: number;
  public gridY: number;
  public type: EntityType;
  protected grid: Grid;
  private debugHighlights: ex.Actor[] = [];
  protected examinedCells: GridPosition[] = [];

  // Optional sprite properties
  public sprite?: ex.Sprite;
  public spriteAnimation?: ex.Animation;

  constructor(options: EntityConfig & { grid: Grid }) {
    super({
      pos: new ex.Vector(options.gridX * TILE_SIZE + TILE_SIZE / 2, options.gridY * TILE_SIZE + TILE_SIZE / 2),
      width: options.width,
      height: options.height,
      color: options.color,
      // Disable collision detection since we handle it ourselves with grid
      collisionType: ex.CollisionType.PreventCollision
    });

    // Create a square background with a thin border
    const square = new ex.Rectangle({
      width: options.width - 2,
      height: options.height - 2,
      color: options.color,
      strokeColor: ex.Color.Black,
      lineWidth: 1
    });

    // Use the colored square as the base graphic
    this.graphics.use(square);

    // Initialize all entities to face left by default
    // We use positive scale for left-facing and negative scale for right-facing
    this.scale.x = Math.abs(this.scale.x); // Ensure positive X scale (facing left)

    this.gridX = options.gridX;
    this.gridY = options.gridY;
    this.type = options.type;
    this.grid = options.grid;
  }

  // Move entity to a new grid position using the centralized movement system
  public moveToGrid(gridX: number, gridY: number) {
    // Save old grid position for proper update
    const oldGridX = this.gridX;
    const oldGridY = this.gridY;

    // Update entity properties - we do this first to keep game logic consistent
    this.gridX = gridX;
    this.gridY = gridY;

    // Update grid reference - this updates the logical position in the grid
    this.grid.moveEntity(this, oldGridX, oldGridY, gridX, gridY);

    // Get the movement system directly - now that we've imported it in Game.ts
    const movementSystem = (window as any).__movementSystem;
    if (movementSystem) {
      movementSystem.moveEntityToGrid(this, gridX, gridY);
    } else {
      // Fallback to direct teleport if system not available
      const targetX = gridX * TILE_SIZE + TILE_SIZE / 2;
      const targetY = gridY * TILE_SIZE + TILE_SIZE / 2;
      this.pos = new ex.Vector(targetX, targetY);
    }

    // If we're being followed, log the movement
    if (this.followIndicator) {
      console.log(`Entity moved to (${gridX}, ${gridY}), indicator follows automatically`);
    }
  }

  // Set the emoji (for entities that use emoji representation)
  public setEmoji(emoji: string) {
    // DISABLED: Emojis are hidden, but code preserved for future use

    // Create a centered label to display the emoji
    /*
    const emojiLabel = new ex.Label({
      text: emoji,
      pos: new ex.Vector(0, 0),
      font: new ex.Font({
        size: Math.min(this.width, this.height) * 0.8,
        family: 'Arial',
        unit: ex.FontUnit.Px
      }),
      color: ex.Color.Black,
      textAlign: ex.TextAlign.Center
    });

    // Center the label on the entity
    emojiLabel.pos = new ex.Vector(-this.width / 2, -this.height / 2);

    // Add the label as a child to the actor
    this.addChild(emojiLabel);
    */

    // Store the emoji in a property in case we want to restore it later
    this._emojiText = emoji;
  }

  // Store emoji text for potential future use
  private _emojiText: string = '';

  // Set a sprite for the entity
  public setSprite(sprite: ex.Sprite) {
    // Set the sprite
    this.sprite = sprite;

    // Use the sprite as the graphic
    this.graphics.use(sprite);

    // Preserve the current flip direction when setting a new sprite
    // If already flipped (facing right), keep it flipped
    if (this.scale.x < 0) {
      // Already facing right, keep it that way
      this.scale.x = -Math.abs(this.scale.x);
    } else {
      // Facing left (default), keep it that way
      this.scale.x = Math.abs(this.scale.x);
    }

    // Clear any existing emoji or other graphics
    this.children.forEach(child => {
      if (child instanceof ex.Label) {
        this.removeChild(child);
      }
    });
  }

  // Set an animation for the entity
  public setAnimation(animation: ex.Animation) {
    // Set the animation
    this.spriteAnimation = animation;

    // Use the animation as the graphic
    this.graphics.use(animation);

    // Preserve the current flip direction when setting a new animation
    // If already flipped (facing right), keep it flipped
    if (this.scale.x < 0) {
      // Already facing right, keep it that way
      this.scale.x = -Math.abs(this.scale.x);
    } else {
      // Facing left (default), keep it that way
      this.scale.x = Math.abs(this.scale.x);
    }

    // Clear any existing emoji or other graphics
    this.children.forEach(child => {
      if (child instanceof ex.Label) {
        this.removeChild(child);
      }
    });
  }

  // Highlight cells that were examined during decision making (for debugging)
  public highlightExaminedCells() {
    // Clear previous highlights
    this.clearHighlights();

    // Create highlight for each examined cell
    for (const cell of this.examinedCells) {
      const highlight = new ex.Actor({
        pos: new ex.Vector(
          cell.x * TILE_SIZE + TILE_SIZE / 2,
          cell.y * TILE_SIZE + TILE_SIZE / 2
        ),
        width: TILE_SIZE,
        height: TILE_SIZE,
        color: ex.Color.fromHex('#ffcc0033'), // Semi-transparent yellow
        collisionType: ex.CollisionType.PreventCollision // Disable collision detection
      });

      this.grid.game.add(highlight);
      this.debugHighlights.push(highlight);
    }

    // Schedule removal of highlights
    setTimeout(() => this.clearHighlights(), 800);
  }

  // Visual indicator that this entity is being followed by the camera
  private followIndicator: ex.Actor | null = null;

  public highlightAsFollowed() {
    // Create a highlight border as a child of this entity so it moves with it
    const highlightBorder = new ex.Actor({
      pos: new ex.Vector(0, 0), // Centered on parent
      width: this.width + 10,
      height: this.height + 10,
      color: ex.Color.Transparent,
      z: -1, // Display behind the entity
      collisionType: ex.CollisionType.PreventCollision // Disable collision detection
    });

    // Add a rectangle graphic with a border (using orange for better visibility)
    const rect = new ex.Rectangle({
      width: Math.abs(this.width) + 8,
      height: Math.abs(this.height) + 8,
      color: ex.Color.Transparent,
      strokeColor: ex.Color.fromHex('#ffaa33'), // Orange-yellow highlight
      lineWidth: 2
    });

    highlightBorder.graphics.use(rect);

    // When the parent is flipped, we don't want the highlight to flip
    // Set a property that will be checked in an event handler
    (highlightBorder as any).isHighlight = true;

    this.addChild(highlightBorder); // Add as child so it follows entity
    this.followIndicator = highlightBorder;

    console.log("Entity highlight added - now following this entity");

    // Add a transform event handler to keep the highlight unflipped
    this.on('postupdate', () => {
      if (this.followIndicator) {
        // Always maintain a scale of 1 for the highlight, regardless of parent scale
        this.followIndicator.scale = new ex.Vector(1, 1);
      }
    });
  }

  // Override the standard update method to handle highlight scaling
  public override update(engine: ex.Engine, delta: number) {
    super.update(engine, delta);

    // Extra check to ensure highlight maintains proper scale
    if (this.followIndicator) {
      this.followIndicator.scale = new ex.Vector(1, 1);
    }
  }

  // Clear all debug highlights
  private clearHighlights() {
    // Clear any standalone highlights (not children)
    for (const highlight of this.debugHighlights) {
      this.grid.game.remove(highlight);
    }
    this.debugHighlights = [];
    this.examinedCells = [];

    // Also clear any follow indicator
    this.clearFollowHighlight();
  }

  // Public method to clear just the follow highlight
  public clearFollowHighlight() {
    // Remove the follow indicator if it exists
    if (this.followIndicator) {
      this.removeChild(this.followIndicator);
      this.followIndicator = null;
    }
  }

  // Determine next action - override in subclasses
  public getNextAction(): Action | null {
    return {
      type: ActionType.Wait,
      entity: this,
      execute: () => {
        console.log(`${this.constructor.name} waits`);
      }
    };
  }
}
